import type { SignalFilter, SignalSeverity } from './signal-model'
import { STAGE_NONE } from './signal-queue'

/**
 * The paged Flat list's server filter (docs/signals.md §Flat). `signals/list`
 * takes `addfilters` clauses `{filter_key, filter_condition, filter_operand,
 * filter_value}`; the handler chains them with their conditions, so every
 * clause here is AND except the keyless entity search, which the server
 * fans out over every suspect/target/signal key as one OR group.
 *
 * `filter_key` is the DB column — inferred from the row's `$Field` names
 * (Postgres folds case), UNVERIFIED against live. `openOnly` is local triage
 * state and never reaches the server.
 */

export interface AddFilter {
  filter_key?: string
  filter_condition: 'AND' | 'OR' | 'NOT'
  filter_operand: string
  filter_value: unknown
}

/** What the Flat list asks the server for, besides the window, tab and page. */
export interface FlatQuery {
  filter: SignalFilter
  stages: readonly string[]
  /** Legacy `?wbId=` — the detection's workbook id ($SourceId). */
  wbId: string | null
  /** Chart range drag, ms epoch [start, end). */
  range: { start: number; end: number } | null
}

/** Severity labels as rows spell them ("High"); info has three spellings. */
const SEVERITY_WIRE: Record<SignalSeverity, string[]> = {
  critical: ['Critical', 'critical', 'CRITICAL'],
  high: ['High', 'high', 'HIGH'],
  medium: ['Medium', 'medium', 'MEDIUM'],
  low: ['Low', 'low', 'LOW'],
  info: ['Info', 'info', 'Informational', 'None'],
}

const inList = (key: string, values: readonly string[]): AddFilter => ({
  filter_key: key,
  filter_condition: 'AND',
  filter_operand: 'IN',
  filter_value: [...values],
})

/**
 * CB-67: the handler pastes `sourcestream` values into the SQL text by
 * f-string (`ARRAY{filter_value}` — a Python list repr), so no client-side
 * escaping is safe: a quote in a value closes the literal whatever we send.
 * Until the server binds it, only names a stream can actually have go out —
 * stream names are bare table names (docs/sql-dialect.md §3; every captured
 * name is `[A-Z0-9_-]`). Anything else is left out of the server filter and
 * the page says so (`unsafeStreams`).
 */
const SAFE_STREAM = /^[A-Za-z0-9_.-]+$/

export const isSafeStream = (name: string) => SAFE_STREAM.test(name)

/** The picked streams the Flat list cannot send (CB-67) — the page names them. */
export const unsafeStreams = (streams: readonly string[]) => streams.filter((s) => !isSafeStream(s))

/** LIKE wildcards in the analyst's text match literally. */
const escapeLike = (v: string) => v.replace(/[\\%_]/g, (c) => `\\${c}`)

/** `falsepositive = flagged`: false hides false positives; true is the False positives queue. */
export const fpFilter = (flagged: boolean): AddFilter => ({
  filter_key: 'falsepositive',
  filter_condition: 'AND',
  filter_operand: '=',
  filter_value: flagged,
})

export function toAddFilters(q: FlatQuery): AddFilter[] {
  const f = q.filter
  const out: AddFilter[] = []
  if (q.wbId)
    out.push({
      filter_key: 'SourceId',
      filter_condition: 'AND',
      filter_operand: '=',
      filter_value: q.wbId,
    })
  if (f.severities.length)
    out.push(
      inList(
        'DetectionSeverity',
        f.severities.flatMap((s) => SEVERITY_WIRE[s]),
      ),
    )
  if (f.tactics.length) out.push(inList('DetectionTactic', f.tactics))
  if (f.detections.length) out.push(inList('DetectionName', f.detections))
  // The server rewrites the operand to array overlap (`&&`) for this key; CB-67 guard above.
  const streams = f.streams.filter(isSafeStream)
  if (streams.length) out.push(inList('sourcestream', streams))
  // tag_list: array overlap with the row's general + campaign tags.
  if (f.tags.length) out.push(inList('tag_list', f.tags))
  if (f.fpOnly || f.hideFp) out.push(fpFilter(Boolean(f.fpOnly)))
  const named = q.stages.filter((s) => s !== STAGE_NONE)
  if (named.length) out.push(inList('sourcestage', named))
  // 'none' alone = no stage; mixed with named stages it cannot be OR-ed in (clauses chain with AND).
  else if (q.stages.includes(STAGE_NONE))
    out.push({
      filter_key: 'sourcestage',
      filter_condition: 'AND',
      filter_operand: 'is',
      filter_value: null,
    })
  const entity = f.entity.trim()
  if (entity)
    out.push({
      filter_condition: 'OR',
      filter_operand: 'ILIKE',
      filter_value: `%${escapeLike(entity)}%`,
    })
  return out
}

/** `signals/list` absolute bounds: `YYYY-MM-DDTHH:mm:ss`, sent with timezone UTC. */
export function wireTime(ms: number): string {
  return new Date(ms).toISOString().slice(0, 19)
}
